import React, { memo, useCallback, useEffect, useState } from "react";
import styles from "./BuildingTypeForm.module.scss";
import { RoofType } from "../../../../model/solar-recommendation-form/roofType";
import { useLazyRoofTypesQuery } from "../../../../service/roof-type-service";
import { toast } from "react-toastify";
import { BuildingType } from "../../../../model/building-type";
import CommonSelect, { CommonSelectItem } from "../../../../common/components/select/CommonSelect";

type Props = {
	selectedRoofType?: RoofType;
	setSelectedRoofType: (roofType: RoofType) => void;
	floorsNumber: number;
	setFloorsNumber: (floors: number) => void;
};

const BuildingTypeForm: React.FC<Props> = memo((props: Props) => {
	const { selectedRoofType, setSelectedRoofType, floorsNumber, setFloorsNumber } = props;
	const [roofTypes, setRoofTypes] = useState<RoofType[]>([]);

	const [buildingType, setBuildingType] = useState<BuildingType>();

	const isBungalowSelected = buildingType === "Bungalow";
	const isStoryBuildingSelected = buildingType === "Story Building";

	const increment = (): void => {
		setFloorsNumber(floorsNumber + 1);
	};

	const decrement = (): void => {
		setFloorsNumber(Math.max(floorsNumber - 1, 1));
	};

	const [getRoofTypes, { isLoading }] = useLazyRoofTypesQuery();

	const fetchRoofTypes = useCallback(async () => {
		if (buildingType) {
			try {
				let response = await getRoofTypes(buildingType).unwrap();
				setRoofTypes(response);
                
                if(selectedRoofType === undefined || selectedRoofType === null){
                    setSelectedRoofType(response[0]);
                }

			} catch (error) {
				toast.error("Error loading Roof Types");
			}
		}
	}, [getRoofTypes, buildingType, selectedRoofType, setSelectedRoofType]);

	useEffect(() => {
		fetchRoofTypes();
	}, [fetchRoofTypes]);

	return (
		<div className={styles.container}>
			<div className={styles.property}>
				Building Type
				<div className={styles.options}>
					<div className={`${styles.option} ${isBungalowSelected ? styles.selected : ""}`} onClick={() => setBuildingType("Bungalow")}>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M7.75 19.25H5.75C4.09315 19.25 2.75 17.9069 2.75 16.25V7.75C2.75 6.09315 4.09315 4.75 5.75 4.75H18.25C19.9069 4.75 21.25 6.09315 21.25 7.75V16.25C21.25 17.9069 19.9069 19.25 18.25 19.25H16.25M7.75 19.25V13.75C7.75 12.0931 9.09315 10.75 10.75 10.75H12M7.75 19.25H12M16.25 19.25V13.75C16.25 12.0931 14.9069 10.75 13.25 10.75H12M16.25 19.25H12M12 10.75V19.25"
								stroke="black"
								stroke-width="1.5"
								stroke-linejoin="round"
							/>
						</svg>
						Bungalow
					</div>
					<div className={`${styles.option} ${isStoryBuildingSelected ? styles.selected : ""}`} onClick={() => setBuildingType("Story Building")}>
						<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M5.5 20.25V6.75C5.5 5.09315 6.84315 3.75 8.5 3.75H17C18.6569 3.75 20 5.09315 20 6.75V20.25M5.5 20.25H20M5.5 20.25H3.5M20 20.25H22M9.5 7.75H11M14.5 7.75H16M9.5 11.75H11M14.5 11.75H16M9.5 15.75H11M14.5 15.75H16"
								stroke="black"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						Story
					</div>
				</div>
			</div>

			{isStoryBuildingSelected && (
				<div className={`${styles.property} ${styles.row}`}>
					Floors
					<div className={styles.counter}>
						<div className={styles.counterButton} onClick={decrement}>
							<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M7.25 12H17.75" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						</div>
						<input className={styles.counterValue} type="number" min={1} value={floorsNumber} />
						<div className={styles.counterButton} onClick={increment}>
							<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M12.5 6C12.9142 6 13.25 6.33579 13.25 6.75V11.25H17.75C18.1642 11.25 18.5 11.5858 18.5 12C18.5 12.4142 18.1642 12.75 17.75 12.75H13.25V17.25C13.25 17.6642 12.9142 18 12.5 18C12.0858 18 11.75 17.6642 11.75 17.25V12.75H7.25C6.83579 12.75 6.5 12.4142 6.5 12C6.5 11.5858 6.83579 11.25 7.25 11.25H11.75V6.75C11.75 6.33579 12.0858 6 12.5 6Z"
									fill="black"
								/>
							</svg>
						</div>
					</div>
				</div>
			)}

			{ (isBungalowSelected || isStoryBuildingSelected )&& <div className={styles.property}>
					RoofType
					<CommonSelect
                        searchable
						selected={(() => {
                            const roofType = roofTypes.find(x => x.id === selectedRoofType?.id)
                            
                            if(roofType === undefined || roofType === null){
                                return null;
                            }

                            return new CommonSelectItem(roofType.id, roofType.name);
                        })()}
						onChange={e => setSelectedRoofType(roofTypes.find(x => x.id === e?.key)!)}
						placeholder="Select Roof Type"
						items={roofTypes.map((x: RoofType) => new CommonSelectItem(x.id, x.name))}
					/>
				</div>
			}
		</div>
	);
});

export default BuildingTypeForm;
